<template>
  <div>
    <idf-info-card :kind="level" icon="function-warning">
      <p class="idf-info-card__text" :style="{ '-webkit-line-clamp': lines }" v-html="text" />
      <a class="idf-info-card__read-more" @click="showModalRestrictedExplosivePrecursor">
        {{ i18n._('Lire la suite') }}
      </a>
    </idf-info-card>
    <adn-modal ref="modalRestrictedExplosivePrecursor" size="normal">
      <template #body>
        <adn-message :kind="level" icon="function-warning">
          <p v-html="text" />
          <template v-if="showTechnicianButton" #actions>
            <adn-button @click="showTechnicianModal" kind="secondary" data-test-button-contact-tc>
              {{ i18n._('Contacter mon') }} {{ i18n._('technicien') }}
            </adn-button>
          </template>
        </adn-message>
      </template>
    </adn-modal>
    <p-contact-technical-sale-modal
      ref="contactTCModal"
      :contact-list="technicalSales"
      data-test="contact-technical-sale-modal"
    ></p-contact-technical-sale-modal>
  </div>
</template>

<script lang="ts">
import { type PropType, computed, ref, defineComponent } from 'vue'
import IdfInfoCard from '@b2ag/design-system/src/components/IdfInfoCard/IdfInfoCard.vue'
import AdnModal from '@b2ag/design-system/src/components/AdnModal/AdnModal.vue'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'
import AdnMessage from '@b2ag/design-system/src/components/AdnMessage/AdnMessage.vue'
import { i18n } from '@b2ag/locale'
import PContactTechnicalSaleModal from '@b2ag/offers/src/presenters/PContactTechnicalSaleModal/PContactTechnicalSaleModal.vue'
import type { TechnicalSale } from '../typings'

export default defineComponent({
  name: 'PProductExplosivesPrecursorCard',
  components: {
    IdfInfoCard,
    AdnModal,
    AdnMessage,
    PContactTechnicalSaleModal,
    AdnButton,
  },
  props: {
    cooperativeName: {
      type: String,
      required: true,
    },
    isExplosivesPrecursorAllowed: {
      type: Boolean,
      required: true,
    },
    lines: {
      type: Number,
      default: 1,
      validator: (lines: number) => lines > 0 && Number.isInteger(lines),
    },
    technicalSales: {
      type: Array as PropType<TechnicalSale[]>,
      default: () => [],
    },
  },
  setup(props) {
    const { isExplosivesPrecursorAllowed, technicalSales, cooperativeName } = props

    const modalRestrictedExplosivePrecursor = ref<(typeof AdnModal & { show: () => void; hide: () => void }) | null>(
      null,
    )
    const contactTCModal = ref<(typeof PContactTechnicalSaleModal & { show: () => void }) | null>(null)

    const level = computed(() => (isExplosivesPrecursorAllowed ? 'warning' : 'danger'))
    const showTechnicianButton = computed(() => technicalSales?.length > 0)
    const text = computed(() => {
      const prefix = `<strong>${i18n._(
        "Précurseurs d’explosifs : votre certificat de déclaration n'est pas valide",
      )}</strong>, ${i18n._(
        'merci de contacter {cooperativeName} afin de pouvoir commander les produits soumis à la réglementation UE 2019/1148 sur les engrais à base de nitrates.',
        { cooperativeName: cooperativeName || 'votre partenaire' },
      )}<br><br>`
      const base = `
        ${i18n._('Le règlement (UE) 2019/1148 du 20 juin 2019 relatif à la commercialisation et à l’utilisation des précurseurs d’explosifs est entré en vigueur le 1er février 2021. Il réglemente les engrais à base de nitrates (')}
        <a href="https://eur-lex.europa.eu/legal-content/FR/TXT/PDF/?uri=CELEX:32019R1148" target="_blank">${i18n._('voir le règlement')}</a>).
        <br><br>
        ${i18n._('L’achat de ce type d’engrais fait l’objet de restrictions et est subordonné à des vérifications entre l’acheteur et le vendeur (notamment preuve de l’identité du client potentiel, activité commerciale, adresse, numéro TVA, utilisation prévue des précurseurs d’explosifs faisant l’objet de restrictions).')}
        <br><br>
        ${i18n._('Le règlement (UE) 2019/1148 prévoit notamment la signature d’une déclaration relative à l’usage des produits faisant l’objet de restrictions. Nous vous invitons donc à vous rapprocher de votre vendeur afin de valider avec lui l’ensemble des procédures nécessaires à la validation et à la livraison de votre commande.')}
      `
      return isExplosivesPrecursorAllowed ? base : `${prefix}\n${base}`
    })

    const showModalRestrictedExplosivePrecursor = () => {
      modalRestrictedExplosivePrecursor.value?.show()
    }
    const showTechnicianModal = () => {
      contactTCModal.value?.show()
      modalRestrictedExplosivePrecursor.value?.hide()
    }

    return {
      modalRestrictedExplosivePrecursor,
      contactTCModal,
      level,
      showTechnicianButton,
      text,
      showModalRestrictedExplosivePrecursor,
      showTechnicianModal,
      i18n,
    }
  },
})
</script>

<style lang="scss" scoped>
:deep(.adn-message-text) {
  padding: 0 0.5rem 0 3rem;
  margin-top: -2.4rem;
}

.idf-info-card__text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;

  @media (max-width: $breakpoint-sm) {
    -webkit-line-clamp: 2;
  }
}

.idf-info-card__read-more {
  color: var(--color-main);
  font-weight: bold;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--color-main-dark);
  }
}
</style>
